<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Performance Appraisal Reports & Analytics</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/hr-dashboard">HR Dashboard</router-link></li>
                    <li class="breadcrumb-item active">Reports</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters Card -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="row align-items-end">
                    <div class="col-md-3">
                      <label class="form-label">Select Year</label>
                      <select v-model="filters.year" @change="loadReports" class="form-select">
                        <option value="">All Years</option>
                        <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Department</label>
                      <select v-model="filters.department" @change="loadReports" class="form-select">
                        <option value="">All Departments</option>
                        <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Status</label>
                      <select v-model="filters.status" @change="loadReports" class="form-select">
                        <option value="">All Status</option>
                        <option value="Draft">Draft</option>
                        <option value="Self-Assessment Complete">Self-Assessment Complete</option>
                        <option value="Appraiser Review Complete">Appraiser Review Complete</option>
                        <option value="Approved">Approved</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <button @click="exportReport" class="btn btn-success w-100 mb-2" :disabled="exporting">
                        <i class="mdi mdi-file-delimited me-1"></i>
                        {{ exporting ? 'Exporting...' : 'Export CSV' }}
                      </button>
                      <button @click="exportToExcel" class="btn btn-outline-success w-100" :disabled="exporting">
                        <i class="mdi mdi-microsoft-excel me-1"></i>
                        Export Excel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading reports...</p>
          </div>

          <!-- Reports Content -->
          <div v-else>

            <!-- Summary Statistics -->
            <!-- Summary Statistics -->
            <div class="row mb-4">
              <div class="col-md-3">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-grow-1">
                        <p class="text-muted mb-2">Total Appraisals</p>
                        <h3 class="mb-0">{{ statistics.total }}</h3>
                      </div>
                      <div class="flex-shrink-0">
                        <div class="avatar-sm rounded-circle bg-primary mini-stat-icon">
              <span class="avatar-title rounded-circle bg-primary">
                <i class="bx bx-file font-size-24"></i>
              </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-3">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-grow-1">
                        <p class="text-muted mb-2">Completed</p>
                        <h3 class="mb-0 text-success">{{ statistics.completed }}</h3>
                        <small class="text-muted">{{ statistics.completedPercent }}%</small>
                      </div>
                      <div class="flex-shrink-0">
                        <div class="avatar-sm rounded-circle bg-success mini-stat-icon">
              <span class="avatar-title rounded-circle bg-success">
                <i class="bx bx-check-circle font-size-24"></i>
              </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-3">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-grow-1">
                        <p class="text-muted mb-2">Pending</p>
                        <h3 class="mb-0 text-warning">{{ statistics.pending }}</h3>
                        <small class="text-muted">{{ statistics.pendingPercent }}%</small>
                      </div>
                      <div class="flex-shrink-0">
                        <div class="avatar-sm rounded-circle bg-warning mini-stat-icon">
              <span class="avatar-title rounded-circle bg-warning">
                <i class="bx bx-time-five font-size-24"></i>
              </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-3">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-grow-1">
                        <p class="text-muted mb-2">Average Rating</p>
                        <h3 class="mb-0 text-info">{{ statistics.averageRating }}</h3>
                        <small class="text-muted">out of 5.0</small>
                      </div>
                      <div class="flex-shrink-0">
                        <div class="avatar-sm rounded-circle bg-info mini-stat-icon">
              <span class="avatar-title rounded-circle bg-info">
                <i class="bx bx-star font-size-24"></i>
              </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Charts Row -->
            <div class="row mb-4">
              <!-- Department Distribution -->
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">Department-wise Distribution</h5>
                  </div>
                  <div class="card-body">
                    <canvas id="departmentChart" height="300"></canvas>
                  </div>
                </div>
              </div>

              <!-- Status Distribution -->
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">Status Distribution</h5>
                  </div>
                  <div class="card-body">
                    <canvas id="statusChart" height="300"></canvas>
                  </div>
                </div>
              </div>
            </div>

            <!-- Rating Distribution Chart -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">Rating Distribution</h5>
                  </div>
                  <div class="card-body">
                    <canvas id="ratingChart" height="100"></canvas>
                  </div>
                </div>
              </div>
            </div>

            <!-- Department Performance Table -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">Department Performance Summary</h5>
                    <button @click="exportDepartmentReport" class="btn btn-sm btn-outline-success">
                      <i class="mdi mdi-download me-1"></i> Export
                    </button>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered table-hover">
                        <thead class="table-light">
                        <tr>
                          <th>Department</th>
                          <th class="text-center">Total</th>
                          <th class="text-center">Completed</th>
                          <th class="text-center">Pending</th>
                          <th class="text-center">Avg Rating</th>
                          <th class="text-center">Completion %</th>
                          <th class="text-center">Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="dept in departmentPerformance" :key="dept.department">
                          <td><strong>{{ dept.department }}</strong></td>
                          <td class="text-center">{{ dept.total }}</td>
                          <td class="text-center">
                            <span class="badge bg-success">{{ dept.completed }}</span>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-warning">{{ dept.pending }}</span>
                          </td>
                          <td class="text-center">
                            <strong class="text-info">{{ dept.avgRating }}</strong>
                          </td>
                          <td class="text-center">
                            <div class="progress" style="height: 20px;">
                              <div class="progress-bar bg-success"
                                   :style="{ width: dept.completionPercent + '%' }">
                                {{ dept.completionPercent }}%
                              </div>
                            </div>
                          </td>
                          <td class="text-center">
                            <button @click="viewDepartmentDetails(dept.department)"
                                    class="btn btn-sm btn-outline-primary">
                              <i class="mdi mdi-eye"></i> View
                            </button>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Top Performers -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header bg-success text-white">
                    <h5 class="card-title mb-0 text-white">
                      <i class="mdi mdi-trophy me-2"></i>Top Performers
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead class="table-light">
                        <tr>
                          <th width="10%">#</th>
                          <th width="50%">Employee</th>
                          <th width="25%">Department</th>
                          <th width="15%" class="text-center">Rating</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(performer, index) in topPerformers" :key="performer.id">
                          <td>
                            <span v-if="index === 0" class="badge bg-warning">🥇</span>
                            <span v-else-if="index === 1" class="badge bg-secondary">🥈</span>
                            <span v-else-if="index === 2" class="badge bg-danger">🥉</span>
                            <span v-else>{{ index + 1 }}</span>
                          </td>
                          <td>{{ performer.employeeName }}</td>
                          <td>{{ performer.department }}</td>
                          <td class="text-center">
                            <span class="badge bg-success">{{ performer.finalRating }}</span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Rating Distribution by Range -->
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-chart-bar me-2"></i>Rating Distribution by Range
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead class="table-light">
                        <tr>
                          <th>Rating Range</th>
                          <th class="text-center">Count</th>
                          <th class="text-center">Percentage</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                          <td><span class="badge bg-success">4.5 - 5.0 (Outstanding)</span></td>
                          <td class="text-center">{{ ratingDistribution.outstanding }}</td>
                          <td class="text-center">{{ ratingDistribution.outstandingPercent }}%</td>
                        </tr>
                        <tr>
                          <td><span class="badge bg-info">4.0 - 4.4 (Excellent)</span></td>
                          <td class="text-center">{{ ratingDistribution.excellent }}</td>
                          <td class="text-center">{{ ratingDistribution.excellentPercent }}%</td>
                        </tr>
                        <tr>
                          <td><span class="badge bg-primary">3.5 - 3.9 (Good)</span></td>
                          <td class="text-center">{{ ratingDistribution.good }}</td>
                          <td class="text-center">{{ ratingDistribution.goodPercent }}%</td>
                        </tr>
                        <tr>
                          <td><span class="badge bg-warning">3.0 - 3.4 (Average)</span></td>
                          <td class="text-center">{{ ratingDistribution.average }}</td>
                          <td class="text-center">{{ ratingDistribution.averagePercent }}%</td>
                        </tr>
                        <tr>
                          <td><span class="badge bg-danger">Below 3.0 (Needs Improvement)</span></td>
                          <td class="text-center">{{ ratingDistribution.needsImprovement }}</td>
                          <td class="text-center">{{ ratingDistribution.needsImprovementPercent }}%</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Detailed Appraisals List -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">Detailed Appraisal Records</h5>
                    <div>
                      <input
                          v-model="searchQuery"
                          type="text"
                          class="form-control form-control-sm"
                          placeholder="Search employee..."
                          style="width: 250px;"
                      >
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered table-hover table-sm">
                        <thead class="table-light">
                        <tr>
                          <th width="5%">#</th>
                          <th width="20%">Employee</th>
                          <th width="15%">Department</th>
                          <th width="10%">Year</th>
                          <th width="8%">Self Rating</th>
                          <th width="8%">Manager Rating</th>
                          <th width="8%">Final Rating</th>
                          <th width="12%">Status</th>
                          <th width="10%">Submitted</th>
                          <th width="4%">Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(appraisal, index) in filteredAppraisals" :key="appraisal.id">
                          <td>{{ index + 1 }}</td>
                          <td>{{ appraisal.employeeName }}</td>
                          <td>{{ appraisal.department }}</td>
                          <td>{{ appraisal.appraisalYear }}</td>
                          <td class="text-center">
                              <span v-if="appraisal.sectionA_SelfOverallRating" class="badge bg-primary">
                                {{ parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td class="text-center">
                              <span v-if="appraisal.sectionA_AppraiserOverallRating" class="badge bg-info">
                                {{ parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td class="text-center">
                              <span v-if="appraisal.finalOverallRating" class="badge bg-success">
                                {{ parseFloat(appraisal.finalOverallRating).toFixed(2) }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                              <span :class="getStatusBadgeClass(appraisal.status)" class="badge">
                                {{ appraisal.status }}
                              </span>
                          </td>
                          <td>{{ formatDate(appraisal.selfAssessmentDate) }}</td>
                          <td class="text-center">
                            <button
                                @click="viewAppraisal(appraisal.id)"
                                class="btn btn-sm btn-outline-primary">
                              <i class="mdi mdi-eye"></i>
                            </button>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Pagination -->
                    <div class="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        Showing {{ filteredAppraisals.length }} of {{ allAppraisals.length }} records
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
        <!-- end container-fluid -->
      </div>
      <!-- End Page-content -->
    </div>
    <!-- end content-minimize -->
  </div>
  <!-- end layout-wrapper -->
</template>

<script>
import { authUrl, execute } from "@/api";
import NavigationBar from "@/components/Navigation";
import Chart from 'chart.js/auto';

export default {
  name: "HRReports",

  components: {
    NavigationBar
  },

  data() {
    return {
      loading: false,
      exporting: false,
      searchQuery: '',

      filters: {
        year: new Date().getFullYear(),
        department: '',
        status: ''
      },

      availableYears: [],
      departments: [],

      statistics: {
        total: 0,
        completed: 0,
        pending: 0,
        completedPercent: 0,
        pendingPercent: 0,
        averageRating: '0.00'
      },

      departmentPerformance: [],
      topPerformers: [],
      ratingDistribution: {
        outstanding: 0,
        excellent: 0,
        good: 0,
        average: 0,
        needsImprovement: 0,
        outstandingPercent: 0,
        excellentPercent: 0,
        goodPercent: 0,
        averagePercent: 0,
        needsImprovementPercent: 0
      },

      allAppraisals: [],

      charts: {
        department: null,
        status: null,
        rating: null
      }
    };
  },

  computed: {
    filteredAppraisals() {
      if (!this.searchQuery) return this.allAppraisals;

      const query = this.searchQuery.toLowerCase();
      return this.allAppraisals.filter(a =>
          a.employeeName.toLowerCase().includes(query) ||
          a.department.toLowerCase().includes(query)
      );
    }
  },

  mounted() {
    this.initializeYears();
    this.loadReports();
  },

  beforeUnmount() {
    // Destroy charts on component unmount
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.destroy();
    });
  },

  methods: {
    initializeYears() {
      const currentYear = new Date().getFullYear();
      for (let i = 0; i < 5; i++) {
        this.availableYears.push(currentYear - i);
      }
    },

    async loadReports() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getAppraisalReports");
        data.append("year", this.filters.year);
        data.append("department", this.filters.department);
        data.append("status", this.filters.status);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const reports = res.data.data;

          this.statistics = reports.statistics;
          this.departmentPerformance = reports.departmentPerformance;
          this.topPerformers = reports.topPerformers;
          this.ratingDistribution = reports.ratingDistribution;
          this.allAppraisals = reports.allAppraisals;
          this.departments = reports.departments;

          // Wait for DOM update then render charts
          this.$nextTick(() => {
            this.renderCharts();
          });
        }
      } catch (error) {
        console.error("Failed to load reports:", error);
        this.$toast?.error("Error loading reports") || alert("Error loading reports");
      } finally {
        this.loading = false;
      }
    },

    renderCharts() {
      this.renderDepartmentChart();
      this.renderStatusChart();
      this.renderRatingChart();
    },

    renderDepartmentChart() {
      const ctx = document.getElementById('departmentChart');
      if (!ctx) return;

      if (this.charts.department) {
        this.charts.department.destroy();
      }

      const labels = this.departmentPerformance.map(d => d.department);
      const data = this.departmentPerformance.map(d => d.total);

      this.charts.department = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'Total Appraisals',
            data: data,
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      });
    },

    renderStatusChart() {
      const ctx = document.getElementById('statusChart');
      if (!ctx) return;

      if (this.charts.status) {
        this.charts.status.destroy();
      }

      this.charts.status = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Completed', 'Pending'],
          datasets: [{
            data: [this.statistics.completed, this.statistics.pending],
            backgroundColor: [
              'rgba(75, 192, 192, 0.7)',
              'rgba(255, 206, 86, 0.7)'
            ],
            borderColor: [
              'rgba(75, 192, 192, 1)',
              'rgba(255, 206, 86, 1)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom'
            }
          }
        }
      });
    },

    renderRatingChart() {
      const ctx = document.getElementById('ratingChart');
      if (!ctx) return;

      if (this.charts.rating) {
        this.charts.rating.destroy();
      }

      this.charts.rating = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['Outstanding (4.5-5.0)', 'Excellent (4.0-4.4)', 'Good (3.5-3.9)', 'Average (3.0-3.4)', 'Needs Improvement (<3.0)'],
          datasets: [{
            label: 'Number of Employees',
            data: [
              this.ratingDistribution.outstanding,
              this.ratingDistribution.excellent,
              this.ratingDistribution.good,
              this.ratingDistribution.average,
              this.ratingDistribution.needsImprovement
            ],
            backgroundColor: [
              'rgba(75, 192, 192, 0.7)',
              'rgba(54, 162, 235, 0.7)',
              'rgba(153, 102, 255, 0.7)',
              'rgba(255, 206, 86, 0.7)',
              'rgba(255, 99, 132, 0.7)'
            ],
            borderColor: [
              'rgba(75, 192, 192, 1)',
              'rgba(54, 162, 235, 1)',
              'rgba(153, 102, 255, 1)',
              'rgba(255, 206, 86, 1)',
              'rgba(255, 99, 132, 1)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      });
    },

    async exportReport() {
      this.exporting = true;

      try {
        // Prepare data for export
        const exportData = this.filteredAppraisals.map((appraisal, index) => ({
          '#': index + 1,
          'Employee Name': appraisal.employeeName,
          'Department': appraisal.department,
          'Position': appraisal.positionTitle || '-',
          'Year': appraisal.appraisalYear,
          'Period': `${appraisal.periodStart || ''} to ${appraisal.periodEnd || ''}`,
          'Self Rating (Section A)': appraisal.sectionA_SelfOverallRating ? parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) : '-',
          'Manager Rating (Section A)': appraisal.sectionA_AppraiserOverallRating ? parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) : '-',
          'Self Rating (Section B)': appraisal.sectionB_SelfOverallRating ? parseFloat(appraisal.sectionB_SelfOverallRating).toFixed(2) : '-',
          'Manager Rating (Section B)': appraisal.sectionB_AppraiserOverallRating ? parseFloat(appraisal.sectionB_AppraiserOverallRating).toFixed(2) : '-',
          'Final Overall Rating': appraisal.finalOverallRating ? parseFloat(appraisal.finalOverallRating).toFixed(2) : '-',
          'Status': appraisal.status,
          'Submitted Date': this.formatDate(appraisal.selfAssessmentDate),
          'Manager Review Date': this.formatDate(appraisal.appraiserReviewDate),
          'HR Review Date': this.formatDate(appraisal.hrCeoReviewDate)
        }));

        // Convert to CSV
        this.downloadCSV(exportData, `Appraisal_Report_${this.filters.year || 'All'}_${new Date().toISOString().split('T')[0]}.csv`);

        this.$toast?.success("Report exported successfully!") || alert("Report exported successfully!");
      } catch (error) {
        console.error("Failed to export report:", error);
        this.$toast?.error("Error exporting report") || alert("Error exporting report");
      } finally {
        this.exporting = false;
      }
    },

    async exportDepartmentReport() {
      try {
        // Prepare department summary for export
        const exportData = this.departmentPerformance.map((dept, index) => ({
          '#': index + 1,
          'Department': dept.department,
          'Total Appraisals': dept.total,
          'Completed': dept.completed,
          'Pending': dept.pending,
          'Average Rating': dept.avgRating,
          'Completion %': dept.completionPercent + '%'
        }));

        // Convert to CSV
        this.downloadCSV(exportData, `Department_Performance_${this.filters.year || 'All'}_${new Date().toISOString().split('T')[0]}.csv`);

        this.$toast?.success("Department report exported!") || alert("Department report exported!");
      } catch (error) {
        console.error("Failed to export department report:", error);
        this.$toast?.error("Error exporting report") || alert("Error exporting report");
      }
    },

    downloadCSV(data, filename) {
      if (!data || data.length === 0) {
        this.$toast?.error("No data to export") || alert("No data to export");
        return;
      }

      // Get headers from first object
      const headers = Object.keys(data[0]);

      // Create CSV content
      let csv = headers.join(',') + '\n';

      // Add data rows
      data.forEach(row => {
        const values = headers.map(header => {
          const value = row[header];
          // Escape values that contain commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return '"' + value.replace(/"/g, '""') + '"';
          }
          return value;
        });
        csv += values.join(',') + '\n';
      });

      // Create blob and download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    },

    exportToExcel() {
      this.exporting = true;

      try {
        // Prepare data for Excel export
        const exportData = this.filteredAppraisals.map((appraisal, index) => ({
          '#': index + 1,
          'Employee Name': appraisal.employeeName,
          'Department': appraisal.department,
          'Position': appraisal.positionTitle || '-',
          'Year': appraisal.appraisalYear,
          'Period Start': appraisal.periodStart || '-',
          'Period End': appraisal.periodEnd || '-',
          'Self Rating (A)': appraisal.sectionA_SelfOverallRating ? parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) : '-',
          'Manager Rating (A)': appraisal.sectionA_AppraiserOverallRating ? parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) : '-',
          'Self Rating (B)': appraisal.sectionB_SelfOverallRating ? parseFloat(appraisal.sectionB_SelfOverallRating).toFixed(2) : '-',
          'Manager Rating (B)': appraisal.sectionB_AppraiserOverallRating ? parseFloat(appraisal.sectionB_AppraiserOverallRating).toFixed(2) : '-',
          'Final Rating': appraisal.finalOverallRating ? parseFloat(appraisal.finalOverallRating).toFixed(2) : '-',
          'Status': appraisal.status,
          'Submitted': this.formatDate(appraisal.selfAssessmentDate),
          'Manager Review': this.formatDate(appraisal.appraiserReviewDate),
          'HR Review': this.formatDate(appraisal.hrCeoReviewDate)
        }));

        // Convert to HTML table for Excel
        const headers = Object.keys(exportData[0]);

        let html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">';
        html += '<head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>';
        html += '<x:Name>Appraisal Report</x:Name>';
        html += '<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet>';
        html += '</x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->';
        html += '<meta charset="UTF-8"></head><body>';
        html += '<table border="1">';

        // Add title row
        html += '<tr><td colspan="' + headers.length + '" style="font-size:16px; font-weight:bold; text-align:center; background-color:#4472C4; color:white;">';
        html += 'Performance Appraisal Report - ' + (this.filters.year || 'All Years');
        if (this.filters.department) html += ' - ' + this.filters.department;
        html += '</td></tr>';

        // Add header row
        html += '<tr style="background-color:#D9E1F2; font-weight:bold;">';
        headers.forEach(header => {
          html += '<td>' + header + '</td>';
        });
        html += '</tr>';

        // Add data rows
        exportData.forEach(row => {
          html += '<tr>';
          headers.forEach(header => {
            html += '<td>' + (row[header] || '') + '</td>';
          });
          html += '</tr>';
        });

        html += '</table></body></html>';

        // Create blob and download
        const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        const filename = `Appraisal_Report_${this.filters.year || 'All'}_${new Date().toISOString().split('T')[0]}.xls`;

        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up
        URL.revokeObjectURL(url);

        this.$toast?.success("Excel report exported successfully!") || alert("Excel report exported successfully!");
      } catch (error) {
        console.error("Failed to export to Excel:", error);
        this.$toast?.error("Error exporting to Excel") || alert("Error exporting to Excel");
      } finally {
        this.exporting = false;
      }
    },

    viewDepartmentDetails(department) {
      this.filters.department = department;
      this.loadReports();
    },

    viewAppraisal(id) {
      this.$router.push({
        path: '/hr-appraisal-view',
        query: { id: id }
      });
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Draft': 'bg-secondary',
        'Self-Assessment Complete': 'bg-info',
        'Appraiser Review Pending': 'bg-warning',
        'Appraiser Review Complete': 'bg-warning',
        'Reviewer Pending': 'bg-warning',
        'Reviewer Complete': 'bg-primary',
        'HR/CEO Review Pending': 'bg-primary',
        'Approved': 'bg-success',
        'Rejected': 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.card-animate {
  transition: all 0.3s ease;
}

.card-animate:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.avatar-sm {
  height: 3rem;
  width: 3rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.table > :not(caption) > * > * {
  padding: 0.5rem 0.5rem;
}

.badge {
  font-size: 0.85rem;
}

canvas {
  max-height: 300px;
}
</style>